import axios from "axios";
import Logo from "../../../images/AE-Logo.svg";
import { Link, useNavigate } from 'react-router-dom';
import * as fa from "react-icons/fa";
import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from "../../../components/Modal"; // Import the Modal component
import AlertMessage from "../../../components/AlertMessage"; // Import the AlertMessage component

// Helper to read any cookie by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const Topbar = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [mobileNavActive, setMobileNavActive] = useState(false);
    const [dropdownActive, setDropdownActive] = useState(null);
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false); // State for logout modal
    const [isUpdating, setIsUpdating] = useState(false);
    
    const [alert, setAlert] = useState({ show: false, type: 'success', message: '' }); // State for alert messages
    const [isLoggingOut, setIsLoggingOut] = useState(false); // Track logout state
    const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });

    const showErrorModal = (title, message) => setErrorModal({ show: true, title, message });
    const hideErrorModal = () => setErrorModal({ show: false, title: '', message: '' });

    // New state for notifications
    const [showNotifications, setShowNotifications] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    // Local form data state for the modal - UPDATED with profile fields
    const [localFormData, setLocalFormData] = useState({
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        company_name: '',
        contact_person: ''
    });

    const navRef = useRef(null);
    const API_URL = process.env.REACT_APP_API_URL;



    // React Query for user profile - UPDATED to include profile fields
    const {
        data: user,
        isLoading: userLoading,
        isError: userError,
        refetch: refetchUser
    } = useQuery({
        // Cache key includes userId so it busts automatically when a different user logs in.
        queryKey: ['userProfile', localStorage.getItem('userId') ?? 'anonymous'],
        queryFn: async () => {
            const token = localStorage.getItem('authToken');
            try {
                const response = await axios.get(`${API_URL}/rest-auth/user-profile/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                return response.data;
            } catch (error) {
                if (error.response?.status === 401) {
                    // Ignore 401s within 10 s of a fresh login — stale in-flight race.
                    const storedAt = parseInt(localStorage.getItem('authTokenStoredAt') || '0', 10);
                    if (Date.now() - storedAt < 10000) { throw error; }
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('user');
                    localStorage.removeItem('userId');
                    localStorage.removeItem('username');
                    localStorage.removeItem('is_superuser');
                    localStorage.removeItem('authTokenStoredAt');
                    window.dispatchEvent(new CustomEvent('tokenExpired'));
                }
                throw error;
            }
        },
        staleTime: 3 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 0,                    // don't retry 401s — fail fast
        refetchOnWindowFocus: false,
        refetchOnMount: true,        // always fetch fresh data after login
        enabled: !!localStorage.getItem('authToken'), // never fire without a token
    });

    // React Query for notifications
    const {
        data: notifications = [],
        isLoading: notificationsLoading,
        isRefetching: isRefetchingNotifications
    } = useQuery({
        queryKey: ['notifications'],
        queryFn: async () => {
            const token = localStorage.getItem('authToken');
            if (!token) {
                return [];
            }

            const response = await axios.get(`${API_URL}/api/notifications/`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            return response.data || [];
        },
        // Matches QUERY_CONFIG in AdminDashboardQueries / ClientDashboardQueries exactly.
        // Same queryKey ['notifications'] means the Topbar reads from the same cache slot
        // that the dashboard queries populate — no duplicate network requests.
        staleTime: 3 * 60 * 1000,  // data considered fresh for 3 minutes
        gcTime: 10 * 60 * 1000,    // cache kept for 10 minutes after unmount
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false,      // serve from cache on mount if data is still fresh
        enabled: !!user,
    });

    // Update unreadCount whenever notifications data changes
    useEffect(() => {
        if (notifications && notifications.length > 0) {
            const unread = notifications.filter(n => !n.is_read).length;
            setUnreadCount(unread);
        } else {
            setUnreadCount(0);
        }
    }, [notifications]);

    // Mutation for marking notification as read
    const markAsReadMutation = useMutation({
        mutationFn: async (notificationId) => {
            const token = localStorage.getItem('authToken');
            const csrfToken = getCookie('csrftoken');
            await axios.post(`${API_URL}/api/notifications/${notificationId}/mark_read/`, {}, {
                withCredentials: true,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    ...(csrfToken && { 'X-CSRFToken': csrfToken }),
                }
            });
            return notificationId;
        },
        onMutate: async (notificationId) => {
            queryClient.setQueryData(['notifications'], (oldNotifications = []) =>
                oldNotifications.map(n =>
                    n.id === notificationId ? { ...n, is_read: true } : n
                )
            );

            setUnreadCount(prev => Math.max(0, prev - 1));

            return { previousNotifications: notifications };
        },
        onError: (error, notificationId, context) => {
            queryClient.setQueryData(['notifications'], context.previousNotifications);
            setUnreadCount(context.previousNotifications.filter(n => !n.is_read).length);
            showErrorModal('Failed to Mark as Read', 'An error occurred while marking the notification as read. Please try again.');
        }
    });

    // Mutation for marking all as read
    const markAllAsReadMutation = useMutation({
        mutationFn: async () => {
            const token = localStorage.getItem('authToken');
            const csrfToken = getCookie('csrftoken');
            await axios.post(`${API_URL}/api/notifications/mark_all_read/`, {}, {
                withCredentials: true,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    ...(csrfToken && { 'X-CSRFToken': csrfToken }),
                }
            });
        },
        onMutate: async () => {
            const previousNotifications = notifications;
            queryClient.setQueryData(['notifications'], (oldNotifications = []) =>
                oldNotifications.map(n => ({ ...n, is_read: true }))
            );
            setUnreadCount(0);
            return { previousNotifications };
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['notifications'], context.previousNotifications);
            setUnreadCount(context.previousNotifications.filter(n => !n.is_read).length);
            showErrorModal('Failed to Mark All as Read', 'An error occurred while marking all notifications as read. Please try again.');
        }
    });

    // Mutation for deleting a single notification
    const deleteNotificationMutation = useMutation({
        mutationFn: async (notificationId) => {
            const token = localStorage.getItem('authToken');
            await axios.delete(`${API_URL}/api/notifications/${notificationId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            return notificationId;
        },
        onMutate: async (notificationId) => {
            const previousNotifications = notifications;
            queryClient.setQueryData(['notifications'], (old = []) =>
                old.filter(n => n.id !== notificationId)
            );
            return { previousNotifications };
        },
        onError: (error, notificationId, context) => {
            queryClient.setQueryData(['notifications'], context.previousNotifications);
            showErrorModal('Failed to Delete Notification', 'An error occurred while deleting the notification. Please try again.');
        }
    });

    // Mutation for deleting ALL notifications
    const deleteAllNotificationsMutation = useMutation({
        mutationFn: async () => {
            const token = localStorage.getItem('authToken');
            await axios.delete(`${API_URL}/api/notifications/delete_all/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
        },
        onMutate: async () => {
            const previousNotifications = notifications;
            queryClient.setQueryData(['notifications'], []);
            setUnreadCount(0);
            return { previousNotifications };
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['notifications'], context.previousNotifications);
            setUnreadCount(context.previousNotifications.filter(n => !n.is_read).length);
            showErrorModal('Failed to Delete All Notifications', 'An error occurred while deleting all notifications. Please try again.');
        }
    });

    // Mutation for deleting READ notifications only
    const deleteReadNotificationsMutation = useMutation({
        mutationFn: async () => {
            const token = localStorage.getItem('authToken');
            await axios.delete(`${API_URL}/api/notifications/delete_read/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
        },
        onMutate: async () => {
            const previousNotifications = notifications;
            queryClient.setQueryData(['notifications'], (old = []) =>
                old.filter(n => !n.is_read)
            );
            return { previousNotifications };
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['notifications'], context.previousNotifications);
            showErrorModal('Failed to Delete Read Notifications', 'An error occurred while deleting read notifications. Please try again.');
        }
    });

    // Mutation for updating user profile - FIXED to match backend structure
    const updateProfileMutation = useMutation({
        mutationFn: async (formData) => {
            const token = localStorage.getItem('authToken');

            // Based on the user object structure, the backend expects flat fields
            const payload = {
                username: formData.username,
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                // Flat fields as seen in the user object
                phone: formData.phone,
                address: formData.address,
                // Include business fields - they might be accepted via profile nested object
                profile: {
                    company_name: formData.company_name,
                    contact_person: formData.contact_person
                }
            };
            const csrfToken = getCookie('csrftoken');
            const response = await axios.put(
                `${API_URL}/rest-auth/update-profile/`,
                payload,
                {
                    withCredentials: true,
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        ...(csrfToken && { 'X-CSRFToken': csrfToken }),
                    }
                }
            );
            return response.data;
        },
        onMutate: async (formData) => {
            setIsUpdating(true);
            setAlert({ show: false, type: 'success', message: '' });

            const previousUser = user;

            // Optimistically update the cache
            queryClient.setQueryData(['userProfile'], (oldUser) => ({
                ...oldUser,
                username: formData.username,
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                phone: formData.phone,
                address: formData.address,
                // We'll keep profile if it exists, but the main user object doesn't have it
                profile: {
                    ...oldUser?.profile,
                    company_name: formData.company_name,
                    contact_person: formData.contact_person
                }
            }));

            return { previousUser };
        },
        onSuccess: (data) => {
            // Update the cache with the response data
            queryClient.setQueryData(['userProfile'], data.user);

            // IMPORTANT: Update local form data with the updated user data
            if (data.user) {
                const updatedFormData = {
                    username: data.user.username || '',
                    first_name: data.user.first_name || '',
                    last_name: data.user.last_name || '',
                    email: data.user.email || '',
                    // Get flat fields from user object
                    phone: data.user.phone || '',
                    address: data.user.address || '',
                    // Business fields might be in profile or not returned at all
                    company_name: data.user.company_name || '',
                    contact_person: data.user.contact_person || ''
                };
                setLocalFormData(updatedFormData);
            }

            setAlert({
                show: true,
                type: 'success',
                message: data.message || 'Profile updated successfully!'
            });

            // Don't close modal immediately - let user see the success message

            // Refetch user data to ensure everything is in sync
            refetchUser();
        },
        onError: (error, variables, context) => {

            if (context?.previousUser) {
                queryClient.setQueryData(['userProfile'], context.previousUser);
            }

            let errorMessage = 'Failed to update profile. Please try again.';
            if (error.response?.data) {
                const errors = error.response.data;
                if (typeof errors === 'object') {
                    if (errors.detail) {
                        errorMessage = errors.detail;
                    } else if (errors.username) {
                        errorMessage = Array.isArray(errors.username) ? errors.username[0] : errors.username;
                    } else if (errors.email) {
                        errorMessage = Array.isArray(errors.email) ? errors.email[0] : errors.email;
                    } else if (errors.phone) {
                        errorMessage = `Phone: ${Array.isArray(errors.phone) ? errors.phone[0] : errors.phone}`;
                    } else if (errors.address) {
                        errorMessage = `Address: ${Array.isArray(errors.address) ? errors.address[0] : errors.address}`;
                    } else if (errors.profile) {
                        // Handle profile field errors
                        const profileErrors = errors.profile;
                        if (typeof profileErrors === 'object') {
                            const firstProfileErrorKey = Object.keys(profileErrors)[0];
                            if (firstProfileErrorKey) {
                                const errorValue = profileErrors[firstProfileErrorKey];
                                errorMessage = `${firstProfileErrorKey}: ${Array.isArray(errorValue) ? errorValue[0] : errorValue}`;
                            }
                        }
                    } else {
                        const firstError = Object.values(errors)[0];
                        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
                    }
                } else if (typeof errors === 'string') {
                    errorMessage = errors;
                }
            }

            setAlert({
                show: true,
                type: 'error',
                message: errorMessage
            });

            // Reset local form data to current user data on error
            if (user) {
                setLocalFormData({
                    username: user.username || '',
                    first_name: user.first_name || '',
                    last_name: user.last_name || '',
                    email: user.email || '',
                    phone: user.phone || '',
                    address: user.address || '',
                    company_name: user.company_name || '',
                    contact_person: user.contact_person || ''
                });
            }
        },
        onSettled: () => {
            setIsUpdating(false);
        }
    });

    // Update local form data when modal opens or when user data changes
    useEffect(() => {
        if (showAccountModal && user) {
            // Since the backend returns fields as flat properties on the user object
            const formData = {
                username: user.username || '',
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                phone: user.phone || '',
                address: user.address || '',
                company_name: user.company_name || '',
                contact_person: user.contact_person || ''
            };
            setLocalFormData(formData);
        }
    }, [showAccountModal, user]);



    // Toggle mobile menu
    const toggleMobileNav = () => {
        setMobileNavActive(!mobileNavActive);
        if (dropdownActive) setDropdownActive(null);
        if (showNotifications) setShowNotifications(false);
    };

    // Toggle dropdown
    const toggleDropdown = (index) => {
        if (dropdownActive === index) {
            setDropdownActive(null);
        } else {
            setDropdownActive(index);
            if (showNotifications) setShowNotifications(false);
        }
    };

    // Toggle notifications dropdown.
    // We intentionally do NOT call refetchNotifications() unconditionally here.
    // React Query's staleTime already governs when a background refetch is warranted.
    // Forcing a refetch on every panel open bypasses the cache and hammers the API.
    // If the cache is fresh (< 3 min old) the cached data is shown instantly.
    // If the cache is stale the query will background-refetch automatically.
    const toggleNotifications = () => {
        setShowNotifications(!showNotifications);
        if (dropdownActive) setDropdownActive(null);
    };

    // Handle logout click - show modal instead of immediate logout
    const handleLogoutClick = () => {
        setShowLogoutModal(true);
        setDropdownActive(null);
        setMobileNavActive(false);
        setShowNotifications(false);
    };

    // Perform actual logout
    // In Topbar.js - Update confirmLogout function

    const confirmLogout = async () => {
        setIsLoggingOut(true);

        try {
            const token = localStorage.getItem('authToken');

            // Attempt to call logout API
            try {
                const csrfToken = getCookie('csrftoken');
                await axios.post(`${API_URL}/rest-auth/logout/`, {}, {
                    withCredentials: true,
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        ...(csrfToken && { 'X-CSRFToken': csrfToken }),
                    }
                });
            } catch (apiError) {
            }

            // Clear ALL local storage items
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            localStorage.removeItem('username');
            localStorage.removeItem('quoteFormData');
            localStorage.removeItem('userId');
            localStorage.removeItem('authTokenStoredAt');

            // IMPORTANT: Clear React Query cache BEFORE closing modal
            queryClient.clear(); // This clears ALL cached queries

            // Close the modal
            setShowLogoutModal(false);

            // Show success message
            setAlert({
                show: true,
                type: 'success',
                message: 'You have been successfully logged out!',
                context: 'logout'
            });

        } catch (error) {

            // Still clear everything
            localStorage.clear(); // Clear ALL localStorage
            queryClient.clear();

            setShowLogoutModal(false);

            setAlert({
                show: true,
                type: 'error',
                message: error.response?.data?.detail || 'Failed to logout. Please try again.',
                context: 'logout'
            });
        } finally {
            setIsLoggingOut(false);
        }
    };

    // Cancel logout
    const cancelLogout = () => {
        setShowLogoutModal(false);
    };

    // Close mobile menu when clicking on a link
    const handleLinkClick = () => {
        setMobileNavActive(false);
        setDropdownActive(null);
        setShowNotifications(false);
    };

    // Handle account link click
    const handleAccountClick = (e) => {
        e.preventDefault();
        setDropdownActive(null);
        setMobileNavActive(false);
        setShowNotifications(false);
        setShowAccountModal(true);
        setAlert({ show: false, type: 'success', message: '' });
    };

    // Close account modal
    const closeAccountModal = () => {
        setShowAccountModal(false);
        setAlert({ show: false, type: 'success', message: '' });
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLocalFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        updateProfileMutation.mutate(localFormData);
    };

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        markAsReadMutation.mutate(notificationId);
    };

    // Mark all as read
    const markAllAsRead = async () => {
        markAllAsReadMutation.mutate();
    };

    // Delete a single notification (stop event so the row click doesn't fire)
    const deleteNotification = (e, notificationId) => {
        e.stopPropagation();
        deleteNotificationMutation.mutate(notificationId);
    };

    // Delete all notifications
    const deleteAllNotifications = () => {
        deleteAllNotificationsMutation.mutate();
    };

    // Delete only read notifications
    const deleteReadNotifications = () => {
        deleteReadNotificationsMutation.mutate();
    };

    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }

        if (notification.related_object_type === 'quotation' && notification.related_object_id) {
            const event = new CustomEvent('viewQuotationFromNotification', {
                detail: {
                    quotationId: notification.related_object_id,
                    notificationId: notification.id
                }
            });
            window.dispatchEvent(event);

            setShowNotifications(false);
            setMobileNavActive(false);
        } else if (notification.url) {
            navigate(notification.url);
            setShowNotifications(false);
            setMobileNavActive(false);
        }
    };

    // Close mobile menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (navRef.current && !navRef.current.contains(event.target)) {
                if (!event.target.closest('.mobile-nav-toggle')) {
                    setMobileNavActive(false);
                    setDropdownActive(null);
                    setShowNotifications(false);
                }
            }
        };

        if (mobileNavActive) {
            document.body.classList.add('mobile-nav-active');
        } else {
            document.body.classList.remove('mobile-nav-active');
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.classList.remove('mobile-nav-active');
        };
    }, [mobileNavActive]);

    // Handle alert dismissal and navigation
    const handleAlertDismiss = () => {
        setAlert(prevAlert => {
            if (prevAlert.context === 'logout') {
                // Clear React Query cache AFTER alert is dismissed
                queryClient.clear();
                // Navigate to login
                navigate('/login');
            }
            return { ...prevAlert, show: false };
        });
    };

    // Auto-hide alert after 3 seconds and then navigate (logout only)
    useEffect(() => {
        if (alert.show && alert.context === 'logout') {
            const timer = setTimeout(() => {
                setAlert(prevAlert => ({ ...prevAlert, show: false }));
                // Clear React Query cache AFTER alert is hidden
                queryClient.clear();
                // Navigate to login
                navigate('/login');
            }, 3000); // Show for 3 seconds
            return () => clearTimeout(timer);
        }
    }, [alert.show, alert.context, navigate, queryClient]);
    // Add this useEffect to detect auth changes
    useEffect(() => {
        // Listen for storage changes (in case of login in another tab)
        const handleStorageChange = (e) => {
            if (e.key === 'authToken' || e.key === 'userId') {
                // Auth changed, invalidate queries
                queryClient.invalidateQueries(['userProfile']);
                refetchUser();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [queryClient, refetchUser]);

    // Function to get initials from username or name
    const getUserInitials = () => {
        if (!user) return "AD";

        if (user.username) {
            return user.username.substring(0, 2).toUpperCase();
        } else if (user.first_name || user.last_name) {
            const first = user.first_name ? user.first_name[0] : '';
            const last = user.last_name ? user.last_name[0] : '';
            return (first + last).toUpperCase();
        }
        return "AD";
    };

    // Function to get display name
    const getDisplayName = () => {
        if (!user) return "Loading...";

        if (user.first_name && user.last_name) {
            return `${user.first_name} ${user.last_name}`;
        } else if (user.first_name) {
            return user.first_name;
        } else if (user.last_name) {
            return user.last_name;
        } else if (user.username) {
            return user.username.charAt(0).toUpperCase() + user.username.slice(1);
        }
        return "User";
    };

    // Function to get user role
    const getUserRole = () => {
        if (!user) return "";

        if (user.is_staff) {
            return user.is_superuser ? "Administrator" : "Staff";
        }
        return "Client";
    };

    // Calculate loading state
    const loading = userLoading || (!user && !userError);

    return (
        <>
            <header
                id="header"
                className="header flex items-center fixed top-0 left-0 right-0 z-50 bg-white shadow-md w-full py-4"
            >
                <div className="container-fluid container relative flex items-center justify-between w-full px-4 md:px-6 mx-auto">

                    <Link to="/" className="logo flex items-center" onClick={handleLinkClick}>
                        <img
                            src={Logo}
                            alt="Alternative Energy"
                            className="h-12 w-auto md:h-16 lg:h-20"
                        />
                    </Link>

                    <nav
                        id="navmenu"
                        className="navmenu"
                        ref={navRef}
                    >
                        <ul className={mobileNavActive ? 'block' : 'hidden'}>
                            {/* Home Link */}
                            <li>
                                <Link
                                    to="/home"
                                    onClick={handleLinkClick}
                                >
                                    Home
                                </Link>
                            </li>

                            {/* Notifications Dropdown */}
                            <li className="relative">
                                <Link
                                    onClick={toggleNotifications}
                                    className="relative p-2 text-gray-600 hover:text-gray-800"
                                    disabled={notificationsLoading || isLoggingOut}
                                >
                                    {isRefetchingNotifications ? (
                                        <div className="h-6 w-6 animate-spin rounded-full border-t-2 border-blue-500"></div>
                                    ) : (
                                        <>
                                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                            </svg>
                                            {unreadCount > 0 && (
                                                <span className="relative -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                                                    {unreadCount > 99 ? '99+' : unreadCount}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </Link>

                                {/* Notifications Dropdown Menu */}
                                {showNotifications && (
                                    <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                                        <div className="p-4 border-b border-gray-200">
                                            <div className="flex justify-between items-center">
                                                <h3 className="font-semibold text-gray-800">
                                                    Notifications
                                                    {isRefetchingNotifications && (
                                                        <span className="ml-2 text-xs text-gray-500">Updating...</span>
                                                    )}
                                                </h3>
                                                {unreadCount > 0 && (
                                                    <button
                                                        onClick={markAllAsRead}
                                                        disabled={markAllAsReadMutation.isLoading}
                                                        className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                    >
                                                        {markAllAsReadMutation.isLoading ? 'Processing...' : 'Mark all as read'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="max-h-96 overflow-y-auto">
                                            {notificationsLoading ? (
                                                <div className="p-4 text-center text-gray-500">
                                                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                                                    <p className="mt-2">Loading notifications...</p>
                                                </div>
                                            ) : notifications.length === 0 ? (
                                                <div className="p-4 text-center text-gray-500">
                                                    No notifications
                                                </div>
                                            ) : (
                                                notifications.map((notification) => (
                                                    <div
                                                        key={notification.id}
                                                        className={`group p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${notification.is_read ? 'opacity-75' : 'bg-blue-50'
                                                            }`}
                                                        onClick={() => handleNotificationClick(notification)}
                                                    >
                                                        <div className="flex items-start space-x-3">
                                                            <span className="text-lg">{notification.icon}</span>
                                                            <div className="flex-1">
                                                                <div className="flex justify-between items-start">
                                                                    <h4 className="font-medium text-gray-800">
                                                                        {notification.title}
                                                                    </h4>
                                                                    <div className="flex items-center space-x-1 ml-2 shrink-0">
                                                                        {!notification.is_read && (
                                                                            <span className="inline-block h-2 w-2 rounded-full bg-blue-500"></span>
                                                                        )}
                                                                        {/* Per-notification delete — visible on hover */}
                                                                        <button
                                                                            onClick={(e) => deleteNotification(e, notification.id)}
                                                                            disabled={deleteNotificationMutation.isLoading}
                                                                            title="Delete notification"
                                                                            className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30"
                                                                        >
                                                                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                            </svg>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <p className="text-sm text-gray-600 mt-1">
                                                                    {notification.message}
                                                                </p>
                                                                <div className="flex justify-between items-center mt-2">
                                                                    <span className="text-xs text-gray-500">
                                                                        {notification.time_ago}
                                                                    </span>
                                                                    {notification.url && (
                                                                        <span className="text-xs text-blue-600">
                                                                            View →
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        {notifications.length > 0 && (
                                            <div className="p-3 border-t border-gray-200">
                                                {/* View all link */}
                                                <div className="text-center mb-2">
                                                    <Link
                                                        to="/dashboard/notifications"
                                                        onClick={() => {
                                                            setShowNotifications(false);
                                                            setMobileNavActive(false);
                                                        }}
                                                        className="text-sm text-blue-600 hover:text-blue-800"
                                                    >
                                                        View all notifications
                                                    </Link>
                                                </div>

                                                {/* Bulk delete actions */}
                                                <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                                                    {/* Delete read — only shown when there are read notifications */}
                                                    {notifications.some(n => n.is_read) && (
                                                        <button
                                                            onClick={deleteReadNotifications}
                                                            disabled={deleteReadNotificationsMutation.isLoading}
                                                            className="flex-1 flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-red-500 hover:bg-red-50 rounded px-2 py-1.5 transition-colors disabled:opacity-40"
                                                        >
                                                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                            {deleteReadNotificationsMutation.isLoading ? 'Deleting…' : 'Clear read'}
                                                        </button>
                                                    )}

                                                    {/* Delete all */}
                                                    <button
                                                        onClick={deleteAllNotifications}
                                                        disabled={deleteAllNotificationsMutation.isLoading}
                                                        className="flex-1 flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded px-2 py-1.5 transition-colors disabled:opacity-40"
                                                    >
                                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                        {deleteAllNotificationsMutation.isLoading ? 'Deleting…' : 'Delete all'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </li>

                            {/* Profile Dropdown */}
                            <li className="dropdown">
                                <Link
                                    to="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        toggleDropdown('profile');
                                    }}
                                >
                                    <div className="flex items-center space-x-2">
                                        <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 flex items-center justify-center">
                                            <span className="text-white text-sm font-semibold">
                                                {loading ? "..." : getUserInitials()}
                                            </span>
                                        </div>
                                        <div className="text-left">
                                            <p className="text-sm font-medium">
                                                {loading ? "Loading..." : getDisplayName()}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {loading ? "" : getUserRole()}
                                            </p>
                                        </div>
                                    </div>
                                    <i className={`bi bi-chevron-down toggle-dropdown ml-2 ${dropdownActive === 'profile' ? 'rotate-180' : ''
                                        }`}></i>
                                </Link>
                                <ul className={dropdownActive === 'profile' ? 'dropdown-active' : ''}>
                                    <li>
                                        <button
                                            onClick={handleAccountClick}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                        >
                                            Account
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            onClick={handleLogoutClick}
                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                            disabled={isLoggingOut}
                                        >
                                            {isLoggingOut ? 'Logging out...' : 'Logout'}
                                        </button>
                                    </li>
                                </ul>
                            </li>
                        </ul>

                        <i
                            className="mobile-nav-toggle flex xl:hidden bi bi-list cursor-pointer"
                            onClick={toggleMobileNav}
                        >
                            <fa.FaBars className={mobileNavActive ? 'text-white' : ''} />
                        </i>
                    </nav>

                </div>
            </header>

            {/* Alert Message */}
            {alert.show && (
                <AlertMessage
                    type={alert.type}
                    message={alert.message}
                    duration={3000}
                    onDismiss={handleAlertDismiss}
                    showCloseButton={true}
                />
            )}

            {/* Account/Profile Update Modal - UPDATED with profile fields */}
            {showAccountModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 mt-16">
                    <div className="bg-white rounded-xl shadow-lg max-w-xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">
                                Update Account Information
                            </h3>
                            <button
                                onClick={closeAccountModal}
                                className="text-gray-500 hover:text-gray-700"
                                disabled={isUpdating}
                            >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {/* Update message — uses AlertMessage */}
                            {alert.show && (
                                <AlertMessage
                                    type={alert.type}
                                    message={alert.message}
                                    duration={2500}
                                    onDismiss={() => setAlert({ show: false, type: 'success', message: '' })}
                                    showCloseButton={true}
                                />
                            )}

                            {/* Personal Information Section */}
                            <div className="border-b border-gray-200 pb-2">
                                <h4 className="text-md font-medium text-gray-700">Personal Information</h4>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Username *
                                </label>
                                <input
                                    type="text"
                                    name="username"
                                    value={localFormData.username}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                    disabled={isUpdating}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={localFormData.first_name}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        disabled={isUpdating}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={localFormData.last_name}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        disabled={isUpdating}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address *
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={localFormData.email}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                    disabled={isUpdating}
                                />
                            </div>

                            {/* Contact Information Section */}
                            <div className="border-b border-gray-200 pb-2 pt-4">
                                <h4 className="text-md font-medium text-gray-700">Contact Information</h4>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={localFormData.phone}
                                    onChange={handleInputChange}
                                    placeholder="+27 12 345 6789"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    disabled={isUpdating}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address
                                </label>
                                <textarea
                                    name="address"
                                    value={localFormData.address}
                                    onChange={handleInputChange}
                                    rows="2"
                                    placeholder="Street address, city, postal code"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    disabled={isUpdating}
                                />
                            </div>

                            {/* Business Information Section */}
                            <div className="border-b border-gray-200 pb-2 pt-4">
                                <h4 className="text-md font-medium text-gray-700">Business Information</h4>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company Name
                                </label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={localFormData.company_name}
                                    onChange={handleInputChange}
                                    placeholder="Your company name (if applicable)"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    disabled={isUpdating}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Person
                                </label>
                                <input
                                    type="text"
                                    name="contact_person"
                                    value={localFormData.contact_person}
                                    onChange={handleInputChange}
                                    placeholder="Primary contact person"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    disabled={isUpdating}
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeAccountModal}
                                    disabled={isUpdating}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isUpdating}
                                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isUpdating ? 'Updating...' : 'Update Profile'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Logout Confirmation Modal */}
            {showLogoutModal && (
                <Modal
                    title="Confirm Logout"
                    message="Are you sure you want to logout?"
                    onClick={cancelLogout}
                    altButton={
                        <button
                            onClick={confirmLogout}
                            type="button"
                            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
                            disabled={isLoggingOut}
                        >
                            {isLoggingOut ? 'Logging out...' : 'Logout'}
                        </button>
                    }
                />
            )}

            {errorModal.show && (
                <Modal
                    title={errorModal.title}
                    message={errorModal.message}
                    onClick={hideErrorModal}
                />
            )}
        </>
    );
};
export default Topbar;